import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';

import { PopupCloseReason, PopupEvent } from 'webapp/enums/enums';
import { MD_BREAKPOINT } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { NodeEventData, PopupWrapperBaseArgs } from 'webapp/components/popup/wrapper/base';
import PopupWrapperBaseComponent from 'webapp/components/popup/wrapper/base';

import type PopupModal from 'webapp/models-custom/popup/modal';

interface Args extends PopupWrapperBaseArgs {
	model: PopupModal;
}

interface PopupWrapperModalComponentInterface<T> {
	Args: T;
	Blocks: { default: [] }; // this is needed for yield
}

export default class PopupWrapperModalComponent extends PopupWrapperBaseComponent<
	PopupWrapperModalComponentInterface<Args>
> {
	animateLength = 300;

	private bindNodeEvents(): void {
		const node = this.popupNode;

		if (!node) {
			return;
		}

		const events: NodeEventData[] = [];

		{
			const eventData = {
				node,
				eventName: 'resize.contentchange',
				fn: () => {
					scheduleOnce('afterRender', this, this.reposition);
				},
			};
			events.push(eventData);
		}

		{
			const eventData = {
				node,
				eventName: 'resize.onlyIfTooHigh',
				fn: () => {
					scheduleOnce('afterRender', this, this.reposition);
				},
			};
			events.push(eventData);
		}

		this.bindEvents(events);
	}

	@action
	reposition(onlyIfTooHigh = false): void {
		const node = this.popupNode;

		if (!node || this.args.model.skipFrame || !this.args.model.isAutoCentered) {
			return;
		}

		if (node.classList.contains('popup--fullscreen')) {
			return;
		}

		const contentNode = node.querySelector<HTMLElement>('.content')!,
			innerNode = node.querySelector<HTMLElement>('.popup-inner')!,
			isFullscreen = window.innerWidth < MD_BREAKPOINT, // in sync with @fullWidthModalBreakpoint / @screen_md in styles
			marginFromEdge = isFullscreen ? 15 : 0; // also @fullWidthMargin in popups.less
		let marginFromTopBottom = isFullscreen ? 50 : 0;

		if (!isFullscreen && node.classList.contains('popup--fixed-size')) {
			marginFromTopBottom = (window.innerHeight - node.offsetHeight) / 2;
		}

		let doResize = true,
			isTooHigh = false,
			isTooWide = false;

		let titleHeight = 0;
		{
			const headerNode = node.querySelector<HTMLElement>('.header')!;
			if (headerNode) {
				titleHeight = headerNode.offsetHeight;
			}

			const titleNode = node.querySelector<HTMLElement>('.title')!;
			if (titleNode) {
				titleHeight = titleNode.offsetHeight;
			}

			const modalTabsNode = node.querySelector<HTMLElement>('.modalTabs')!;
			if (modalTabsNode) {
				titleHeight += modalTabsNode.offsetHeight;
			}

			const filterNode = node.querySelector<HTMLElement>('.filter-line')!;
			if (filterNode) {
				titleHeight += filterNode.offsetHeight;
			}

			const controlsNode = node.querySelector<HTMLElement>('.controls2--sticky')!;
			if (controlsNode) {
				titleHeight += controlsNode.offsetHeight;
			}

			const richContentHeaderNode = node.querySelector<HTMLElement>('.rich-header-content')!;
			if (richContentHeaderNode) {
				titleHeight += richContentHeaderNode.offsetHeight;
			}
		}

		if (contentNode) {
			isTooHigh =
				titleHeight + contentNode.scrollHeight >
				window.innerHeight - 2 * marginFromTopBottom;
		}

		if (innerNode) {
			isTooWide = innerNode.scrollWidth > window.innerWidth - 3 * marginFromEdge;
		}

		if (onlyIfTooHigh) {
			doResize = node.offsetHeight + node.offsetTop > window.innerHeight;
		}

		if (!doResize) {
			return;
		}

		node.classList.toggle('tooWide', isTooWide);
		node.classList.toggle('tooHigh', isTooHigh);

		if (innerNode) {
			innerNode.style.maxWidth = isTooWide
				? `${window.innerWidth - 2 * marginFromEdge}px`
				: '';
		}
		if (contentNode) {
			contentNode.style.maxHeight = isTooHigh
				? `${window.innerHeight - titleHeight - 2 * marginFromTopBottom}px`
				: '';
		}

		const left = isFullscreen ? marginFromEdge : (window.innerWidth - node.offsetWidth) / 2,
			top = isFullscreen ? marginFromTopBottom : (window.innerHeight - node.offsetHeight) / 2;

		node.style.top = `${top}px`;
		node.style.left = `${left}px`;
		node.style.minWidth = '0';

		// differentiating between "tooHigh" which refers to the content that is too high (has scrollbar)
		// and "tooHighPopup" when it does not fit in the screen
		// are different when there is a height set for content that is not changing
		// so the content will need scrollbar even if the popup itself fits the window just fine
		// example: modal.source-type-info
		const isTooHighPopup = node.offsetHeight + node.offsetTop > window.innerHeight;
		node.classList.toggle('tooHighPopup', isTooHighPopup);

		if (isTooHighPopup && !isTooWide && !isFullscreen) {
			node.style.minWidth = `${node.offsetWidth + 18}px`; // bcos of vertical scrollbar
		}
	}

	@action
	handleCloseClick() {
		this.close(PopupCloseReason.XCLICK);
	}

	@action
	override setupView(node: HTMLElement): void {
		super.setupView(node);
		this.popupNode = node.querySelector('.modal-popup');
		this.args.model.component = this;

		this.bindNodeEvents();

		this.reposition();
		this.readyToOpen();
		this.setElementToAnimateFrom();
	}

	@action
	setElementToAnimateFrom(): void {
		const elementToAnimateFrom = this.args.model.elementToAnimateFrom;
		if (!elementToAnimateFrom) {
			return;
		}

		// Get the element's position and dimensions
		const rect = elementToAnimateFrom.getBoundingClientRect();
		const originX = rect.left + rect.width / 2;
		const originY = rect.top + rect.height / 2;

		// Set the transform origin for the animation
		this.popupNode!.style.transformOrigin = `${originX}px ${originY}px`;
	}

	@action
	override teardownView(): void {
		super.teardownView();

		const rootNode = this.rootNode;
		if (rootNode) {
			// for closing context popups in case this modal is a parentPopup
			rootNode.dispatchEvent(new Event(PopupEvent.CLOSING));
		}
	}
	<template>
		<div
			class="modal-ct"
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			{{onWindowEvents (fn this.reposition false) "resize" 200}}
		>
			{{#if @model.hasOverlay}}
				<div
					role="button"
					class="modal-overlay"
					{{on "click" this.handleOverlayClick}}
				></div>
			{{/if}}
			{{#if @model.skipFrame}}
				{{yield}}
			{{else}}
				<div
					class={{joinClasses
						(array
							"modal-popup"
							"popup"
							@model.themeClass
							@model.popupClass
							(if @model.isAnimated "animated")
							(if @model.elementToAnimateFrom "animated-from-element")
						)
					}}
					style={{@model.popupStyle}}
				>
					<div class="popup-inner {{if @model.isClosing ' disable-actions'}}">
						{{#if @model.showClose}}
							<a role="button" class="close" {{on "click" this.handleCloseClick}}>
								<CommonUiIconComponent @name="Close" @size="lg" />
							</a>
						{{/if}}
						{{yield}}
					</div>
				</div>
			{{/if}}
		</div>
	</template>
}
